import { ClientConfig } from '../../config/ClientConfig'
import { getCookie } from '../CookieStorageService'

export const GEO_LOCATION_UPDATE = 'geoLocationUpdate'

import { isValidGeoFailureReason } from './geo-failure-reason'
import type { GeoFailureReason } from './geo-failure-reason'

const GEOCOMPLY_SPOOF_QUERY_PARAM = 'geocomply_spoof'
const GEOCOMPLY_SPOOF_COOKIE = 'GEOCOMPLY_SPOOF'
export const GEOCOMPLY_SPOOF_STATUS = 'GEOCOMPLY_SPOOF_STATUS'
export const GEOCOMPLY_SPOOF_REASON = 'GEOCOMPLY_SPOOF_REASON'
export const GEOCOMPLY_SPOOF_ALLOWLIST_USER_IDS = 'GEOCOMPLY_SPOOF_ALLOWLIST_USER_IDS'
export const GEOCOMPLY_SPOOF_ALLOWLIST_EMAIL_PATTERN = 'GEOCOMPLY_SPOOF_ALLOWLIST_EMAIL_PATTERN'

export interface SpoofParamsReader {
	getSearch(): string
	getCookieValue(name: string): string | undefined
}

function defaultReader(): SpoofParamsReader {
	return {
		getSearch: () => window.location.search,
		getCookieValue: (name) => getCookie(name)?.value,
	}
}

/**
 * Returns a spoofed subdivision from query param or cookie for geo-gate testing.
 * Query param takes precedence over cookie.
 * @param reader Optional reader for DI/testing. When omitted, uses window and getCookie.
 * @returns subdivision string (e.g. US-CA) or null if neither present
 */
export function getSpoofSubdivision(reader?: SpoofParamsReader): string | null {
	if (typeof window === 'undefined' && !reader) return null
	const r = reader ?? defaultReader()
	const fromQuery = new URLSearchParams(r.getSearch()).get(GEOCOMPLY_SPOOF_QUERY_PARAM)
	if (fromQuery && fromQuery.trim() !== '') {
		return fromQuery.trim()
	}
	const fromCookie = r.getCookieValue(GEOCOMPLY_SPOOF_COOKIE)?.trim()
	if (fromCookie) {
		return fromCookie
	}
	return null
}

/**
 * Returns the spoofed status from query param or cookie.
 * Query param takes precedence. Returns 'Success' when only subdivision is set (backward compat).
 */
export function getSpoofStatus(reader?: SpoofParamsReader): 'Success' | 'Failed' | null {
	if (typeof window === 'undefined' && !reader) return null
	const r = reader ?? defaultReader()
	const fromQuery = new URLSearchParams(r.getSearch()).get('geocomply_spoof_status')
	const raw = fromQuery?.trim() || r.getCookieValue(GEOCOMPLY_SPOOF_STATUS)?.trim() || null
	if (raw === 'Success' || raw === 'Failed') return raw
	if (raw !== null) return null
	// Backward compat: if subdivision is set but status isn't, treat as Success
	const subdivision = r.getCookieValue(GEOCOMPLY_SPOOF_COOKIE)?.trim()
	if (subdivision) return 'Success'
	return null
}

/**
 * Returns the spoofed failure reason from query param or cookie.
 * Only meaningful when getSpoofStatus() returns 'Failed'.
 */
export function getSpoofFailureReason(reader?: SpoofParamsReader): GeoFailureReason | null {
	if (typeof window === 'undefined' && !reader) return null
	const r = reader ?? defaultReader()
	const fromQuery = new URLSearchParams(r.getSearch()).get('geocomply_spoof_reason')
	const raw = fromQuery?.trim() || r.getCookieValue(GEOCOMPLY_SPOOF_REASON)?.trim() || null
	if (raw && isValidGeoFailureReason(raw)) return raw
	return null
}

let cachedEmailRegex: { pattern: string; regex: RegExp } | null = null

function getEmailRegex(pattern: string): RegExp | null {
	if (!pattern) return null
	if (cachedEmailRegex?.pattern === pattern) return cachedEmailRegex.regex
	try {
		const regex = new RegExp(pattern)
		cachedEmailRegex = { pattern, regex }
		return regex
	} catch {
		return null
	}
}

function decodeCookieValue(value: string): string {
	try {
		return decodeURIComponent(value)
	} catch {
		return value
	}
}

function getEffectiveAllowlistUserIds(): string[] {
	const fromCookie = getCookie(GEOCOMPLY_SPOOF_ALLOWLIST_USER_IDS)?.value
	if (fromCookie !== undefined && fromCookie !== null && fromCookie.trim() !== '') {
		const decoded = decodeCookieValue(fromCookie)
		return decoded
			.split(',')
			.map((s) => s.trim())
			.filter(Boolean)
	}
	return ClientConfig.GEO_COMPLY_SPOOF_USER_IDS ?? []
}

function getEffectiveAllowlistEmailPattern(): string {
	const fromCookie = getCookie(GEOCOMPLY_SPOOF_ALLOWLIST_EMAIL_PATTERN)?.value
	if (fromCookie !== undefined && fromCookie !== null) {
		return decodeCookieValue(fromCookie)
	}
	return ClientConfig.GEO_COMPLY_SPOOF_EMAIL_PATTERN ?? ''
}

/**
 * Returns raw config allowlist values (no cookie override). For geo-debug UI "Config:" hint.
 */
export function getConfigAllowlist(): { userIds: string[]; emailPattern: string } {
	return {
		userIds: ClientConfig.GEO_COMPLY_SPOOF_USER_IDS ?? [],
		emailPattern: ClientConfig.GEO_COMPLY_SPOOF_EMAIL_PATTERN ?? '',
	}
}

/**
 * Returns effective allowlist values (override if set, else config). For geo-debug UI display.
 */
export function getEffectiveAllowlistConfig(): { userIds: string[]; emailPattern: string } {
	return {
		userIds: getEffectiveAllowlistUserIds(),
		emailPattern: getEffectiveAllowlistEmailPattern(),
	}
}

/**
 * Returns true if the user is in the GeoComply spoof allowlist (user IDs or email pattern).
 * Used to permit spoofed geo during geo-gate testing.
 * Override values (when set) take precedence over config.
 */
export function isGeoToolingAllowlisted(userId: string, email?: string): boolean {
	const allowlistIds = getEffectiveAllowlistUserIds()
	const emailPattern = getEffectiveAllowlistEmailPattern()

	if (allowlistIds.includes(userId)) {
		return true
	}

	if (email && emailPattern) {
		const regex = getEmailRegex(emailPattern)
		if (regex?.test(email)) {
			return true
		}
	}

	return false
}

const SUBDIVISION_REGEX = /^[A-Z]{2}-[A-Z0-9]{2,3}$/

export function isValidSubdivision(subdivision: string): boolean {
	return SUBDIVISION_REGEX.test(subdivision)
}

// --- Sequence-based spoof for guidance modal testing ---

export const GEOCOMPLY_SPOOF_SEQUENCE = 'GEOCOMPLY_SPOOF_SEQUENCE'

export type SpoofStep =
	| 'hint:shown'
	| 'hint:skipped'
	| 'success'
	| 'fail:permission'
	| 'fail:integrity'
	| 'fail:error'
	| 'fail:timeout'
	| 'fail:token_failed'
	| 'fail:sdk_load_failed'
	| 'retryable:shown'
	| 'sdk-retry:success'
	| 'sdk-retry:fail'
	| 'our-retry:success'
	| 'our-retry:fail'

export function getSpoofSequence(): SpoofStep[] | null {
	const cookie = getCookie(GEOCOMPLY_SPOOF_SEQUENCE)?.value
	if (!cookie) return null
	try {
		const parsed = JSON.parse(decodeURIComponent(cookie))
		if (Array.isArray(parsed) && parsed.length > 0) return parsed
		return null
	} catch {
		return null
	}
}

/**
 * When guidance modals are OFF, synthesise a GEOCOMPLY_SPOOF_SEQUENCE step from the
 * legacy GEOCOMPLY_SPOOF_STATUS and GEOCOMPLY_SPOOF_REASON cookies so the fake SDK
 * can be used without requiring the sequence to be set explicitly.
 * No-op when no legacy spoof status is configured.
 */
export function synthesiseLegacySpoofSequence(): void {
	const legacyStatus = getSpoofStatus()
	if (!legacyStatus) return
	const reason = getSpoofFailureReason()
	const step: SpoofStep = legacyStatus === 'Success' ? 'success'
		: reason === 'permission_denied' ? 'fail:permission'
		: reason === 'verification_failed' ? 'fail:integrity'
		: reason === 'timeout' ? 'fail:timeout'
		: reason === 'token_failed' ? 'fail:token_failed'
		: reason === 'sdk_load_failed' ? 'fail:sdk_load_failed'
		: 'fail:error'
	document.cookie = `${GEOCOMPLY_SPOOF_SEQUENCE}=${encodeURIComponent(JSON.stringify([step]))}; max-age=86400; path=/`
}

export const GEOCOMPLY_SPOOF_GUIDANCE = 'GEOCOMPLY_SPOOF_GUIDANCE'
export const GEOCOMPLY_SPOOF_FAILURE_CATEGORY = 'GEOCOMPLY_SPOOF_FAILURE_CATEGORY'

const VALID_FAILURE_CATEGORIES: FailureCategory[] = ['permission', 'integrity', 'error']

export function getSpoofFailureCategory(): FailureCategory | null {
	const value = getCookie(GEOCOMPLY_SPOOF_FAILURE_CATEGORY)?.value
	if (!value) return null
	return (VALID_FAILURE_CATEGORIES as string[]).includes(value)
		? value as FailureCategory
		: null
}


export function getSpoofGuidanceContent(key: string): LocationGuidance | null {
	const cookie = getCookie(GEOCOMPLY_SPOOF_GUIDANCE)?.value
	if (!cookie) return null
	try {
		const parsed = JSON.parse(decodeURIComponent(cookie)) as Record<string, unknown>
		const value = parsed[key]
		if (value && typeof value === 'object' && 'title' in value) {
			return value as LocationGuidance
		}
		return null
	} catch {
		return null
	}
}
